// Agent Skills view — /api/v2/agent-skills DB-backed read.
// Sprint 2 PR-D1:
// - Skill seed 46 relation / 13 agent canlı veriden geliyor (PR-C3.A + seed).
// - apiGetJson (401 redirect + 5xx toast).
// - agentName filter, summary chip, nested skill metadata.
// - API hatasında Sprint 1 STATIC_MATRIX fallback (legacy display, kullanıcı boş ekran görmesin).
// - Pure renderAgentSkillsHtml() helper test edilebilir.

import { escHtml } from '../utils.js';
import { apiGetJson } from '../api.js';

// Backend response shape (/api/v2/agent-skills).
export interface AgentSkillRowApi {
  agentName: string;
  skillId: string;
  skillVersion: string;
  status: string;
  confidenceThreshold: string;
  allowedActionLevel: number;
  updatedAt: string;
  skill: {
    name: string;
    category: string;
    policyLevel: number;
    ownerAgent: string;
  } | null;
}

export interface AgentSkillsSummary {
  total: number;
  agents: number;
  byAgent: Record<string, number>;
}

interface AgentSkillsResponse {
  ok: true;
  agentSkills: AgentSkillRowApi[];
  summary: AgentSkillsSummary;
}

// /api/v2/skills response (catalog summary — chip strip için).
export interface SkillCatalogSummary {
  total: number;
  byStatus: Record<string, number>;
  byAgent: Record<string, number>;
  byCategory: Record<string, number>;
}
interface SkillsResponse {
  ok: true;
  skills: unknown[];
  summary: SkillCatalogSummary;
}

// Sprint 1 fallback (API down senaryosu).
const STATIC_MATRIX: Array<{ agent: string; role: string; skills: string[] }> = [
  { agent: 'Ruby',     role: 'Satış / Lead',   skills: ['lead_scoring', 'whatsapp_reply_suggestion', 'followup_strategy', 'objection_handling'] },
  { agent: 'Sapphire', role: 'Fiyat / Teklif', skills: ['quote_intake', 'pricing_check', 'margin_warning', 'quote_draft'] },
  { agent: 'Crystal',  role: 'Fatura',         skills: ['invoice_check', 'billing_summary', 'invoice_risk_check'] },
  { agent: 'Zircon',   role: 'Tahsilat',       skills: ['collection_followup', 'overdue_detection', 'payment_reminder_draft'] },
  { agent: 'Topaz',    role: 'Reklam',         skills: ['ad_performance_analysis', 'campaign_drop_detection', 'audience_suggestion'] },
  { agent: 'Opal',     role: 'Kreatif',        skills: ['creative_brief_generation', 'ad_copy_generation', 'landing_page_copy'] },
  { agent: 'Forge',    role: 'Engineer',       skills: ['code_patch_planning', 'test_generation', 'implementation_review'] },
  { agent: 'Pilot',    role: 'DevOps',         skills: ['deploy_check', 'smoke_test', 'rollback_plan'] },
  { agent: 'Onyx',     role: 'Disiplin',       skills: ['drift_audit', 'policy_check', 'skill_review', 'cross_context_guard'] },
  { agent: 'Lore',     role: 'Araştırma',      skills: ['research_summary', 'market_scan', 'source_validation', 'context_pack_generation', 'wiki_update'] },
  { agent: 'Meter',    role: 'Bütçe',          skills: ['token_budget_check', 'cost_warning', 'model_route_suggestion', 'token_context_reduction'] },
  { agent: 'Nexus',    role: 'GM',             skills: ['task_routing', 'executive_summary', 'decision_escalation', 'decision_recording'] },
  { agent: 'Atlas',    role: 'COO/CFO',        skills: ['operations_review', 'finance_risk_review', 'resource_prioritization'] },
];

type ViewState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; agentSkills: AgentSkillRowApi[]; summary: AgentSkillsSummary; catalog: SkillCatalogSummary | null; filter: string }
  | { kind: 'fallback'; message: string };

let state: ViewState = { kind: 'idle' };
let filter = '';

export async function loadAgentSkills(): Promise<void> {
  state = { kind: 'loading' };
  // Paralel fetch: /agent-skills (matris) + /skills (katalog özet chip strip).
  const [asResult, catResult] = await Promise.all([
    apiGetJson<AgentSkillsResponse>('/api/v2/agent-skills', { agentName: filter || undefined }),
    apiGetJson<SkillsResponse>('/api/v2/skills'),
  ]);
  if (asResult.ok) {
    state = {
      kind: 'ready',
      agentSkills: asResult.data.agentSkills,
      summary: asResult.data.summary,
      catalog: catResult.ok ? catResult.data.summary : null,
      filter,
    };
  } else {
    state = { kind: 'fallback', message: asResult.message ?? asResult.error };
  }
}

export function renderAgentSkills(): void {
  const root = document.getElementById('view-agent-skills');
  if (!root) return;
  root.innerHTML = renderAgentSkillsHtml(state);
  attachFilterHandlers();
}

// Pure render — test edilebilir.
export function renderAgentSkillsHtml(s: ViewState): string {
  const header = `
    <div class="view-header">
      <div>
        <div class="view-title">Agent Skills</div>
        <div class="view-sub">central_brain.agent_skills — canlı DB matrisi</div>
      </div>
    </div>`;
  if (s.kind === 'idle' || s.kind === 'loading') {
    return `${header}<div class="empty-state"><div class="empty-icon">⏳</div><div class="empty-title">Yükleniyor…</div></div>`;
  }
  if (s.kind === 'fallback') {
    return `${header}
      <div class="rt-card" style="border-left:4px solid var(--danger,#e55);background:var(--bg2);">
        <div class="rt-card-title">⚠️ Canlı DB verisi ALINAMADI — aşağıdaki tablo ESKİ statik demo (Sprint 1), gerçek veri DEĞİL</div>
        <div style="color:var(--muted);font-size:12px;margin-bottom:8px;">${escHtml(s.message)}</div>
        <button id="ags-retry" class="btn-primary" style="padding:6px 12px;">Tekrar dene</button>
      </div>
      ${renderStaticMatrix()}`;
  }
  const agentOptions = Object.keys(s.summary.byAgent).sort();
  const catalogStrip = s.catalog ? renderCatalogStrip(s.catalog) : '';
  const filterBar = `
    <div class="rt-card" style="display:flex;gap:8px;align-items:center;">
      <label style="font-size:12px;color:var(--muted);display:flex;flex-direction:column;gap:2px;">
        Agent filter
        <select id="ags-f-agent" class="form-select" style="width:160px;padding:4px 8px;">
          <option value="">(all ${s.summary.agents} agents)</option>
          ${agentOptions.map((a) => `<option value="${escHtml(a)}"${a === s.filter ? ' selected' : ''}>${escHtml(a)} (${s.summary.byAgent[a]})</option>`).join('')}
        </select>
      </label>
      <button id="ags-apply" class="btn-primary" style="padding:6px 12px;">Filtrele</button>
      <div style="margin-left:auto;font-size:12px;color:var(--muted);">
        total <strong style="color:var(--text);">${s.summary.total}</strong> · agents <strong style="color:var(--text);">${s.summary.agents}</strong>
      </div>
    </div>`;
  return `${header}
    ${catalogStrip}
    ${filterBar}
    <div class="rt-card">
      <div class="rt-card-title">Skill atamaları (${s.agentSkills.length})</div>
      ${s.agentSkills.length === 0
        ? '<div class="empty-state"><div class="empty-icon">📭</div><div class="empty-title">Eşleşen kayıt yok</div></div>'
        : `<table style="width:100%;font-size:13px;">
            <thead><tr>
              <th style="text-align:left;">Agent</th>
              <th style="text-align:left;">Skill</th>
              <th style="text-align:left;">Category</th>
              <th style="text-align:left;">Policy</th>
              <th style="text-align:left;">Action lvl</th>
              <th style="text-align:left;">Confidence</th>
              <th style="text-align:left;">Status</th>
            </tr></thead>
            <tbody>
              ${s.agentSkills.map((row) => `
                <tr>
                  <td style="font-weight:600;">${escHtml(row.agentName)}</td>
                  <td><code style="background:var(--bg2);padding:2px 6px;border-radius:4px;">${escHtml(row.skillId)}</code></td>
                  <td>${row.skill ? escHtml(row.skill.category) : '<span style="color:var(--danger,#e55);">skill missing</span>'}</td>
                  <td>${row.skill ? String(row.skill.policyLevel) : '—'}</td>
                  <td>${String(row.allowedActionLevel)}</td>
                  <td>${escHtml(row.confidenceThreshold)}</td>
                  <td>${escHtml(row.status)}</td>
                </tr>`).join('')}
            </tbody>
          </table>`
      }
    </div>`;
}

// Pure — /api/v2/skills katalog özetini chip strip olarak render eder.
export function renderCatalogStrip(cat: SkillCatalogSummary): string {
  const chips = (obj: Record<string, number>) => Object.keys(obj).sort().map((k) =>
    `<span style="background:var(--bg2);padding:3px 8px;border-radius:12px;font-size:11px;margin-right:6px;display:inline-block;margin-bottom:4px;">${escHtml(k)} <strong>${obj[k]}</strong></span>`
  ).join('');
  return `<div class="rt-card">
    <div class="rt-card-title">Skill katalog (${cat.total})</div>
    <div style="display:flex;flex-wrap:wrap;gap:12px;font-size:12px;">
      <div>
        <div style="color:var(--muted);text-transform:uppercase;font-size:10px;margin-bottom:4px;">by category</div>
        <div>${chips(cat.byCategory)}</div>
      </div>
      <div>
        <div style="color:var(--muted);text-transform:uppercase;font-size:10px;margin-bottom:4px;">by status</div>
        <div>${chips(cat.byStatus)}</div>
      </div>
    </div>
  </div>`;
}

function renderStaticMatrix(): string {
  return `<div class="rt-card">
    <div class="rt-card-title">Sprint 1 statik matris (legacy)</div>
    <table style="width:100%;font-size:13px;">
      <thead><tr><th style="text-align:left;">Agent</th><th style="text-align:left;">Rol</th><th style="text-align:left;">Skills</th></tr></thead>
      <tbody>
        ${STATIC_MATRIX.map((r) => `
          <tr>
            <td style="font-weight:600;">${escHtml(r.agent)}</td>
            <td style="color:var(--muted);">${escHtml(r.role)}</td>
            <td>${r.skills.map((s) => `<code style="background:var(--bg2);padding:2px 6px;border-radius:4px;margin:2px;display:inline-block;">${escHtml(s)}</code>`).join(' ')}</td>
          </tr>`).join('')}
      </tbody>
    </table>
  </div>`;
}

function attachFilterHandlers(): void {
  // Fallback state'inde "Tekrar dene" — filter butonundan bağımsız wire et.
  const retry = document.getElementById('ags-retry');
  if (retry) {
    retry.addEventListener('click', async () => {
      await loadAgentSkills();
      renderAgentSkills();
    });
  }
  const btn = document.getElementById('ags-apply');
  if (!btn) return;
  btn.addEventListener('click', async () => {
    filter = (document.getElementById('ags-f-agent') as HTMLSelectElement | null)?.value ?? '';
    await loadAgentSkills();
    renderAgentSkills();
  });
}
